import React, { useCallback, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
} from "@material-ui/core";
import { getFieldProps } from "../../utils";
import styled from "styled-components/macro";
import { useFormikContext } from "formik";
import MarkdownEditorV2 from "../../shared/MarkdownEditorV2";
//import InfoTooltip from "../../InfoTooltip";

import ImageUpload from "../../ImageUpload";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import { useAppContext } from "../../../api/AppContext";
import FileUpload from "../../shared/FileUpload";
import { uploadFile } from "../../../api";
import bytes from "bytes";
import { DocumentContainer } from "../../Welfares/steps/Doubts";
import {
  getCompletionState,
  hasNewsBlockLanguageContent,
  LANGUAGE_LABELS,
} from "../../shared/languageConfig";
import LanguageSelector from "../../shared/LanguageSelector";
import { usePreferredLanguagePersistence } from "../../shared/usePreferredLanguagePersistence";

const ContentBlockContainer = styled.div`
  margin: 2rem 0;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: flex-start;
`;

const DeleteButton = styled(IconButton)`
  position: absolute;
  right: 0;
  top: 0;
`;

const DragHandleContainer = styled.div`
  cursor: move;
  display: flex;
  align-items: center;
  margin-right: 1rem;
  padding: 0.5rem;
`;

const ContentBlockContent = styled.div`
  flex: 1;
`;

const BuildMarkdownEditor = ({
  formik,
  placeholder,
  name,
  value,
  columnMd = 6,
}) => {
  const onChange = useCallback(
    (value) => {
      formik.setFieldValue(name, value);
      formik.setFieldTouched(name, true);
    },
    [formik, name]
  );

  return (
    <Grid item md={columnMd} xs={12}>
      <MarkdownEditorV2
        key={name} // To remount when language changes
        error={false}
        label={``}
        placeHolder={placeholder}
        {...getFieldProps(formik, {
          name,
        })}
        useHeadings={true}
        value={value}
        onChange={onChange}
      />
    </Grid>
  );
};

// hacky way to allow file upload for Allitude
const ALLITUDE_COMPANY_ID = "21330221-0072-45a1-88e1-d6ad6de3bf40";
const getCustomPathInfo = (user) => {
  if (user.companies?.includes(ALLITUDE_COMPANY_ID)) {
    return {
      journeyId:
        process.env.REACT_APP_DASHBOARD_STAGE === "qa"
          ? "afb4b4d1-26de-40fe-b336-fc5167b76204"
          : "7b9743a2-fae7-4a03-95a1-0d62bfe1839a",
    };
  } else {
    return { cid: "67df6292-c0a7-4831-a94e-61b6ea73c990" };
  }
};

const NewsText = () => {
  const formik = useFormikContext();
  const [showAddBlockDialog, setShowAddBlockDialog] = useState(false);
  const [newBlockType, setNewBlockType] = useState("markdown");
  const [isFileUploading, setIsFileUploading] = useState(null);
  const [availabilityErrors, setAvailabilityErrors] = useState({});
  const { user } = useAppContext();
  const customPathInfo = getCustomPathInfo(user);
  const changePreferredLanguage = usePreferredLanguagePersistence();
  const validLanguages = formik.values.validLanguages || ["it", "en"];
  const preferredLanguage =
    formik.values.uiPreferredLanguage || validLanguages[0];
  const secondaryLanguages = validLanguages.filter(
    (language) => language !== preferredLanguage
  );
  const activeSecondaryLanguage =
    formik.values.uiActiveSecondaryLanguage || secondaryLanguages[0] || null;
  const contentColumnMd = secondaryLanguages.length ? 6 : 12;

  const languageCompletionByCode = useMemo(() => {
    const contentBlocks = formik.values.content || [];
    const totalBlocks = contentBlocks.length || 1;

    return validLanguages.reduce((acc, language) => {
      const completed = contentBlocks.reduce((total, block) => {
        return total + (hasNewsBlockLanguageContent(block, language) ? 1 : 0);
      }, 0);

      acc[language] = getCompletionState({
        completedLanguage: completed,
        isActive: language === activeSecondaryLanguage,
        totalRequiredFields: totalBlocks,
      });
      return acc;
    }, {});
  }, [activeSecondaryLanguage, formik.values.content, validLanguages]);

  const handleAddBlock = useCallback(() => {
    const initialDataByLanguage = validLanguages.reduce((acc, language) => {
      acc[language] =
        newBlockType === "markdown"
          ? ""
          : newBlockType === "attachment"
          ? { name: null, url: null }
          : null;
      return acc;
    }, {});

    const newBlock = {
      data: initialDataByLanguage,
      type: newBlockType,
    };

    const currentContent = formik.values.content || [];
    formik.setFieldValue("content", [...currentContent, newBlock]);
    formik.setFieldTouched("content", true, false);
    setShowAddBlockDialog(false);
    setNewBlockType("markdown");
  }, [formik, newBlockType, validLanguages]);

  const handleBlockChange = useCallback(
    (index, field, value) => {
      const contentArray = Array.isArray(formik.values.content)
        ? [...formik.values.content]
        : [];
      const existingBlock = contentArray[index] || {};
      const updatedBlock = {
        ...existingBlock,
        data: {
          ...(existingBlock.data || {}),
          [field]: value,
        },
      };
      contentArray[index] = updatedBlock;
      formik.setFieldValue("content", contentArray);
      formik.setFieldTouched("content", true, false);
    },
    [formik]
  );

  const handleDocumentChange = useCallback(
    async (args, index, field) => {
      const uploadKey = `${index}-${field}`;
      setIsFileUploading(uploadKey);
      setAvailabilityErrors((prev) => ({ ...prev, [uploadKey]: null }));

      try {
        const data = await uploadFile({
          ...args,
          customPathInfo,
        });

        const contentArray = Array.isArray(formik.values.content)
          ? [...formik.values.content]
          : [];
        const existingBlock = contentArray[index] || {};
        const updatedBlock = {
          ...existingBlock,
          data: {
            ...(existingBlock.data || {}),
            [field]: { name: args.name, url: data.url },
          },
        };
        contentArray[index] = updatedBlock;
        formik.setFieldValue("content", contentArray);
        formik.setFieldTouched("content", true, false);
      } catch (error) {
        setAvailabilityErrors((prev) => ({
          ...prev,
          [uploadKey]: error.message || "Upload failed",
        }));
      } finally {
        setIsFileUploading(null);
      }
    },
    [customPathInfo, formik]
  );

  const handleDocumentReset = useCallback(
    async (index, field) => {
      const contentArray = Array.isArray(formik.values.content)
        ? [...formik.values.content]
        : [];
      const uploadKey = `${index}-${field}`;
      const existingBlock = contentArray[index] || {};
      const updatedBlock = {
        ...existingBlock,
        data: {
          ...(existingBlock.data || {}),
          [field]: { name: null, url: null },
        },
      };
      contentArray[index] = updatedBlock;
      formik.setFieldValue("content", contentArray);
      formik.setFieldTouched("content", true, false);
      setAvailabilityErrors((prev) => ({ ...prev, [uploadKey]: null }));
    },
    [formik]
  );

  const buildDocumentUpload = useCallback(
    (index, field, formikValue, columnMd = 6) => {
      return (
        <Grid item md={columnMd} xs={12}>
          <FileUpload
            accept=".pdf, .doc, .odt, .xlsx, .xls, .ods"
            maxSize={bytes("10 MB")}
            readAs="DataURL"
            onChange={(val) => handleDocumentChange(val, index, field)}
          >
            {({ upload, onUpload, error }) => {
              return (
                <>
                  <DocumentContainer
                    buttonProps={{ children: "Upload", fullWidth: true }}
                    caption={
                      <Typography
                        color="textSecondary"
                        style={{ color: "#BFBFBF" }}
                        variant="body2"
                      >
                        Upload document(s) (.pdf, .word, .odt, .xlsx, .xls,
                        .ods) ({LANGUAGE_LABELS[field] || field.toUpperCase()})
                      </Typography>
                    }
                    containerStyles={{}}
                    disabled={false}
                    document={formikValue}
                    error={
                      error || availabilityErrors[`${index}-${field}`] || null
                    }
                    handleRemove={() => handleDocumentReset(index, field)}
                    isLoading={isFileUploading === `${index}-${field}`}
                    isUploading={isFileUploading === `${index}-${field}`}
                    styles={{}}
                    onStartUpload={upload}
                    onUpload={onUpload}
                  />
                </>
              );
            }}
          </FileUpload>
        </Grid>
      );
    },
    [handleDocumentChange, handleDocumentReset, isFileUploading]
  );

  const handleDeleteBlock = useCallback(
    (index) => () => {
      const currentContent = [...formik.values.content];
      currentContent.splice(index, 1);
      formik.setFieldValue("content", currentContent);
      formik.setFieldTouched("content", true, false);
    },
    [formik]
  );

  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) {
        return;
      }

      const items = Array.from(formik.values.content || []);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      formik.setFieldValue("content", items);
      formik.setFieldTouched("content", true, false);
    },
    [formik]
  );

  const renderContentColumn = (block, index, language) => {
    if (!language) {
      return null;
    }

    if (block.type === "markdown") {
      return (
        <BuildMarkdownEditor
          columnMd={contentColumnMd}
          formik={formik}
          name={`content[${index}].data.${language}`}
          placeholder={LANGUAGE_LABELS[language] || language.toUpperCase()}
          value={block.data?.[language]}
        />
      );
    }

    if (block.type === "image") {
      return (
        <Grid item md={contentColumnMd} xs={12}>
          <ImageUpload
            key={`image-${index}-${language}`}
            buttonProps={{ children: "Upload" }}
            caption={
              <Typography
                color="textSecondary"
                style={{ color: "#BFBFBF" }}
                variant="body2"
              >
                Upload image (
                {LANGUAGE_LABELS[language] || language.toUpperCase()}) (.png,
                .jpg, .jpeg)
              </Typography>
            }
            containerStyles={{ height: 250 }}
            customPathInfo={customPathInfo}
            maxSize={15 * 1024 * 1024}
            styles={{ maxHeight: 250, width: "unset" }}
            value={block.data?.[language]}
            onChange={(obj) => handleBlockChange(index, language, obj.url)}
            onReset={() => handleBlockChange(index, language, null)}
          />
        </Grid>
      );
    }

    if (block.type === "attachment") {
      return buildDocumentUpload(
        index,
        language,
        block.data?.[language],
        contentColumnMd
      );
    }

    return null;
  };

  return (
    <Grid item xs={12}>
      <Grid
        container
        alignItems="center"
        direction="column"
        style={{ marginBottom: "1.5rem" }}
      >
        <Grid item style={{ marginBottom: "1rem", textAlign: "center" }}>
          <Typography variant="h6">
            In this section you can add the body of your news!
          </Typography>
        </Grid>

        <LanguageSelector
          align="center"
          languageCompletionByCode={languageCompletionByCode}
          preferredLanguage={preferredLanguage}
          style={{ marginBottom: 0 }}
          validLanguages={validLanguages}
          onActiveSecondaryLanguageChange={(language) =>
            formik.setFieldValue("uiActiveSecondaryLanguage", language)
          }
          onPreferredLanguageChange={(nextPreferred, nextSecondary) => {
            formik.setFieldValue("uiPreferredLanguage", nextPreferred);
            formik.setFieldValue("uiActiveSecondaryLanguage", nextSecondary);
            changePreferredLanguage(nextPreferred);
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Grid container justifyContent="center" style={{ marginTop: "1rem" }}>
          <Button
            color="primary"
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setShowAddBlockDialog(true)}
          >
            Add Content Block
          </Button>
        </Grid>
        {/* Content Blocks */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="content-blocks">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {(formik.values.content || []).map((block, index) => (
                  <Draggable
                    key={index}
                    draggableId={`block-${index}`}
                    index={index}
                  >
                    {(provided) => (
                      <ContentBlockContainer
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <DragHandleContainer {...provided.dragHandleProps}>
                          <DragHandleIcon />
                        </DragHandleContainer>
                        <ContentBlockContent>
                          <DeleteButton onClick={handleDeleteBlock(index)}>
                            <DeleteIcon />
                          </DeleteButton>
                          <Grid container spacing={2}>
                            {renderContentColumn(
                              block,
                              index,
                              preferredLanguage
                            )}
                            {renderContentColumn(
                              block,
                              index,
                              activeSecondaryLanguage
                            )}
                          </Grid>
                        </ContentBlockContent>
                      </ContentBlockContainer>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Grid>

      <Dialog
        open={showAddBlockDialog}
        onClose={() => setShowAddBlockDialog(false)}
      >
        <DialogTitle>Add Content Block</DialogTitle>
        <DialogContent>
          <Select
            fullWidth
            value={newBlockType}
            onChange={(e) => setNewBlockType(e.target.value)}
          >
            <MenuItem value="markdown">Markdown</MenuItem>
            <MenuItem value="image">Image</MenuItem>
            <MenuItem value="attachment">Attachment</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddBlockDialog(false)}>Cancel</Button>
          <Button color="primary" onClick={handleAddBlock}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default NewsText;
